import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API, { formatError } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
    Hash, MessageSquare, Users, Plus, Send, Search, X
} from "lucide-react";

function Avatar({ user, size = 32 }) {
    const [imgError, setImgError] = useState(false);
    const initial = (user?.nickname || user?.name || "?")[0]?.toUpperCase();
    const bg = user?.avatar || "#002FA7";
    const url = user?.avatar_url;
    if (url && !imgError) {
        return (
            <div className="rounded-full flex-shrink-0 overflow-hidden" style={{ width: size, height: size }}>
                <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" onError={() => setImgError(true)} />
            </div>
        );
    }
    return (
        <div className="rounded-full flex items-center justify-center flex-shrink-0"
            style={{ width: size, height: size, backgroundColor: bg }}>
            <span className="text-white font-bold" style={{ fontSize: size * 0.42 }}>{initial}</span>
        </div>
    );
}

// Memoized message list - doesn't re-render when input text changes
const MessageList = memo(function MessageList({ messages, currentUserId }) {
    const msgEndRef = useRef(null);
    useEffect(() => {
        msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    if (messages.length === 0) {
        return (
            <div className="flex-1 overflow-y-auto p-4">
                <div className="text-center py-12 text-[#9CA3AF]">
                    <MessageSquare size={40} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Nu sunt mesaje încă. Spune salut!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {messages.map((m, i) => {
                const prev = messages[i - 1];
                const showAuthor = !prev || prev.user_id !== m.user_id || (new Date(m.created_at) - new Date(prev.created_at)) > 5 * 60 * 1000;
                const isMine = m.user_id === currentUserId;
                return (
                    <div key={m.id} className={`flex gap-2 ${showAuthor ? "mt-3" : ""}`}>
                        <div className="w-8 flex-shrink-0">
                            {showAuthor && <Avatar user={m.author} size={32} />}
                        </div>
                        <div className="flex-1 min-w-0">
                            {showAuthor && (
                                <div className="flex items-baseline gap-2">
                                    <span className={`text-sm font-bold ${isMine ? "text-[#002FA7] dark:text-[#6A91FF]" : "text-[#111827] dark:text-[#F3F4F6]"}`}>
                                        {m.author?.nickname || m.author?.name || "User"}
                                    </span>
                                    <span className="text-[10px] text-[#9CA3AF]">
                                        {new Date(m.created_at).toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                </div>
                            )}
                            <p className="text-sm text-[#111827] dark:text-[#F3F4F6] whitespace-pre-wrap break-words">{m.content}</p>
                        </div>
                    </div>
                );
            })}
            <div ref={msgEndRef} />
        </div>
    );
});

// Chat input isolated so that typing does not re-render the message list
const ChatInput = memo(function ChatInput({ channelName, onSend }) {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const inputRef = useRef(null);

    const submit = async (e) => {
        e.preventDefault();
        const content = text.trim();
        if (!content || sending) return;
        setSending(true);
        setText("");
        try {
            await onSend(content);
            inputRef.current?.focus();
        } catch (err) {
            setText(content);
        } finally {
            setSending(false);
        }
    };

    return (
        <form onSubmit={submit} className="border-t border-[#E4E4E7] dark:border-[#1E232B] bg-white dark:bg-[#0F1317] p-3 flex items-center gap-2">
            <input
                ref={inputRef}
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={`Scrie în ${channelName}...`}
                data-testid="chat-input"
                autoComplete="off"
                className="flex-1 px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            <button type="submit" disabled={sending || !text.trim()}
                data-testid="chat-send-btn"
                className="bg-[#002FA7] text-white px-3 py-2 rounded-md hover:bg-[#002482] disabled:opacity-60">
                <Send size={16} />
            </button>
        </form>
    );
});

export default function ChatPage() {
    const { channelId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = ["super_admin", "admin"].includes(user?.role);

    const [channels, setChannels] = useState([]);
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeChannel, setActiveChannel] = useState(null);
    const [messages, setMessages] = useState([]);
    const [showNewDM, setShowNewDM] = useState(false);
    const [showNewChannel, setShowNewChannel] = useState(false);
    const [dmSearch, setDmSearch] = useState("");
    const [newChannelForm, setNewChannelForm] = useState({ name: "", team_id: "", member_ids: [] });
    const [saving, setSaving] = useState(false);

    const wsRef = useRef(null);
    const activeChannelIdRef = useRef(null);

    useEffect(() => {
        activeChannelIdRef.current = activeChannel?.id || null;
    }, [activeChannel?.id]);

    const fetchChannels = useCallback(() => {
        return API.get("/chat/channels").then(r => {
            setChannels(r.data);
            return r.data;
        });
    }, []);

    useEffect(() => {
        fetchChannels().catch(() => toast.error("Eroare la încărcarea chat-ului")).finally(() => setLoading(false));
        API.get("/users").then(r => setUsers(r.data)).catch(() => {});
        API.get("/teams").then(r => setTeams(r.data)).catch(() => {});
    }, [fetchChannels]);

    // Select default channel when loaded
    useEffect(() => {
        if (channelId) {
            const ch = channels.find(c => c.id === channelId);
            if (ch && ch.id !== activeChannel?.id) setActiveChannel(ch);
        } else if (channels.length > 0 && !activeChannel) {
            setActiveChannel(channels[0]);
            navigate(`/chat/${channels[0].id}`, { replace: true });
        }
    }, [channels, channelId]); // eslint-disable-line

    // Fetch messages when channel changes
    useEffect(() => {
        if (!activeChannel) return;
        setMessages([]);
        API.get(`/chat/channels/${activeChannel.id}/messages`).then(r => {
            setMessages(r.data);
        }).catch(err => toast.error(formatError(err)));
    }, [activeChannel?.id]); // eslint-disable-line

    // WebSocket for real-time
    useEffect(() => {
        if (!user) return;
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const wsUrl = base.replace(/^http/, "ws") + "/api/ws/chat";
        try {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;
            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.event === "new_message") {
                        if (activeChannelIdRef.current === data.channel_id) {
                            setMessages(prev => {
                                if (prev.some(m => m.id === data.message.id)) return prev;
                                return [...prev, data.message];
                            });
                        }
                        // Update unread badges locally without full refetch
                        setChannels(prev => prev.map(c => {
                            if (c.id !== data.channel_id) return c;
                            if (activeChannelIdRef.current === c.id) return c;
                            return { ...c, unread_count: (c.unread_count || 0) + 1 };
                        }));
                    }
                } catch {}
            };
            ws.onclose = () => { wsRef.current = null; };
            ws.onerror = () => {};
            return () => {
                try { ws.close(); } catch {}
            };
        } catch {}
    }, [user]);

    // Mark channel as read when switched (clear unread badge locally)
    useEffect(() => {
        if (!activeChannel) return;
        setChannels(prev => prev.map(c => c.id === activeChannel.id ? { ...c, unread_count: 0 } : c));
    }, [activeChannel?.id]);

    const sendMessage = useCallback(async (content) => {
        const ch = activeChannel;
        if (!ch) return;
        const r = await API.post(`/chat/channels/${ch.id}/messages`, { content });
        setMessages(prev => prev.some(m => m.id === r.data.id) ? prev : [...prev, r.data]);
    }, [activeChannel]);

    const createDM = async (otherId) => {
        try {
            const r = await API.post("/chat/channels", { type: "dm", member_ids: [otherId] });
            toast.success("Conversație deschisă");
            setShowNewDM(false);
            const updated = await fetchChannels();
            const ch = updated.find(c => c.id === r.data.id);
            if (ch) {
                setActiveChannel(ch);
                navigate(`/chat/${ch.id}`);
            }
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const createChannel = async (e) => {
        e.preventDefault();
        if (!newChannelForm.name.trim()) { toast.error("Nume canal obligatoriu"); return; }
        setSaving(true);
        try {
            const r = await API.post("/chat/channels", {
                type: "channel",
                name: newChannelForm.name.trim(),
                team_id: newChannelForm.team_id || null,
                member_ids: newChannelForm.member_ids
            });
            toast.success("Canal creat");
            setShowNewChannel(false);
            setNewChannelForm({ name: "", team_id: "", member_ids: [] });
            const updated = await fetchChannels();
            const ch = updated.find(c => c.id === r.data.id);
            if (ch) setActiveChannel(ch);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const groupedChannels = useMemo(() => {
        return {
            team: channels.filter(c => c.type === "team"),
            channel: channels.filter(c => c.type === "channel"),
            dm: channels.filter(c => c.type === "dm"),
        };
    }, [channels]);

    return (
        <div className="flex h-full bg-[#F8F9FA] dark:bg-[#0B0D10]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {/* Channel sidebar */}
            <div className="w-64 flex-shrink-0 bg-white dark:bg-[#0F1317] border-r border-[#E4E4E7] dark:border-[#1E232B] flex flex-col">
                <div className="h-14 border-b border-[#E4E4E7] dark:border-[#1E232B] px-4 flex items-center justify-between">
                    <span className="font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Chat</span>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-3">
                    {/* Team channels */}
                    <div>
                        <div className="flex items-center justify-between px-2 py-1">
                            <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Echipe</span>
                        </div>
                        {groupedChannels.team.map(c => <ChannelItem key={c.id} channel={c} active={activeChannel?.id === c.id} onClick={() => { setActiveChannel(c); navigate(`/chat/${c.id}`); }} />)}
                        {groupedChannels.team.length === 0 && <p className="text-xs text-[#9CA3AF] px-2 italic">Nu ești într-o echipă</p>}
                    </div>

                    {/* Custom channels */}
                    <div>
                        <div className="flex items-center justify-between px-2 py-1">
                            <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Canale</span>
                            {isAdmin && (
                                <button onClick={() => setShowNewChannel(true)} className="text-[#9CA3AF] hover:text-[#002FA7] rounded p-0.5"
                                    data-testid="chat-new-channel-btn" title="Canal nou">
                                    <Plus size={13} />
                                </button>
                            )}
                        </div>
                        {groupedChannels.channel.map(c => <ChannelItem key={c.id} channel={c} active={activeChannel?.id === c.id} onClick={() => { setActiveChannel(c); navigate(`/chat/${c.id}`); }} />)}
                        {groupedChannels.channel.length === 0 && <p className="text-xs text-[#9CA3AF] px-2 italic">Fără canale</p>}
                    </div>

                    {/* DMs */}
                    <div>
                        <div className="flex items-center justify-between px-2 py-1">
                            <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Mesaje directe</span>
                            <button onClick={() => setShowNewDM(true)} className="text-[#9CA3AF] hover:text-[#002FA7] rounded p-0.5"
                                data-testid="chat-new-dm-btn" title="DM nou">
                                <Plus size={13} />
                            </button>
                        </div>
                        {groupedChannels.dm.map(c => <ChannelItem key={c.id} channel={c} active={activeChannel?.id === c.id} onClick={() => { setActiveChannel(c); navigate(`/chat/${c.id}`); }} />)}
                        {groupedChannels.dm.length === 0 && <p className="text-xs text-[#9CA3AF] px-2 italic">Fără conversații</p>}
                    </div>
                </div>
            </div>

            {/* Chat panel */}
            <div className="flex-1 flex flex-col min-w-0">
                {activeChannel ? (
                    <>
                        <div className="h-14 border-b border-[#E4E4E7] dark:border-[#1E232B] bg-white dark:bg-[#0F1317] flex items-center px-4">
                            <div className="flex-shrink-0 mr-3">
                                {activeChannel.type === "team" && <Users size={18} className="text-[#002FA7] dark:text-[#6A91FF]" />}
                                {activeChannel.type === "channel" && <Hash size={18} className="text-[#002FA7] dark:text-[#6A91FF]" />}
                                {activeChannel.type === "dm" && <Avatar user={activeChannel.display_user} size={28} />}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-[#111827] dark:text-[#F3F4F6]">{activeChannel.display_name}</p>
                                {activeChannel.team_name && <p className="text-xs text-[#9CA3AF]">Echipa: {activeChannel.team_name}</p>}
                            </div>
                        </div>

                        <MessageList messages={messages} currentUserId={user?.id} />

                        <ChatInput channelName={activeChannel.display_name} onSend={sendMessage} />
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-[#9CA3AF]">
                        {loading ? "Se încarcă..." : "Selectează un canal"}
                    </div>
                )}
            </div>

            {/* New DM modal */}
            {showNewDM && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowNewDM(false)} />
                    <div className="relative bg-white dark:bg-[#151A21] rounded-lg shadow-2xl w-full max-w-md z-10 border border-[#E4E4E7] dark:border-[#252B33]">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E4E7] dark:border-[#252B33]">
                            <h2 className="font-bold text-[#111827] dark:text-[#F3F4F6]">Mesaj direct nou</h2>
                            <button onClick={() => setShowNewDM(false)} className="text-[#9CA3AF] hover:text-[#111827] dark:hover:text-white"><X size={18} /></button>
                        </div>
                        <div className="p-4">
                            <div className="relative mb-3">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                                <input value={dmSearch} onChange={e => setDmSearch(e.target.value)}
                                    placeholder="Caută persoană..."
                                    className="w-full pl-8 pr-3 py-2 border border-[#E4E4E7] rounded-md text-sm" />
                            </div>
                            <div className="max-h-80 overflow-y-auto">
                                {users.filter(u => u.id !== user?.id && (u.name?.toLowerCase().includes(dmSearch.toLowerCase()) || u.email?.toLowerCase().includes(dmSearch.toLowerCase()))).map(u => (
                                    <button key={u.id} onClick={() => createDM(u.id)}
                                        data-testid={`dm-user-${u.id}`}
                                        className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-[#F1F3F5] dark:hover:bg-[#1C222A] text-left">
                                        <Avatar user={u} size={32} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-[#111827] dark:text-[#F3F4F6] truncate">{u.name}</p>
                                            <p className="text-xs text-[#9CA3AF] truncate">{u.email}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* New channel modal */}
            {showNewChannel && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowNewChannel(false)} />
                    <div className="relative bg-white dark:bg-[#151A21] rounded-lg shadow-2xl w-full max-w-md z-10 border border-[#E4E4E7] dark:border-[#252B33]">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E4E7] dark:border-[#252B33]">
                            <h2 className="font-bold text-[#111827] dark:text-[#F3F4F6]">Canal nou</h2>
                            <button onClick={() => setShowNewChannel(false)} className="text-[#9CA3AF] hover:text-[#111827] dark:hover:text-white"><X size={18} /></button>
                        </div>
                        <form onSubmit={createChannel} className="p-4 space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume *</label>
                                <input value={newChannelForm.name} onChange={e => setNewChannelForm({ ...newChannelForm, name: e.target.value })} required
                                    data-testid="new-channel-name"
                                    placeholder="ex. anunțuri, proiect-x"
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Echipă (opțional)</label>
                                <select value={newChannelForm.team_id} onChange={e => setNewChannelForm({ ...newChannelForm, team_id: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm">
                                    <option value="">— Fără echipă —</option>
                                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Membri</label>
                                <select multiple value={newChannelForm.member_ids} onChange={e => {
                                    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
                                    setNewChannelForm({ ...newChannelForm, member_ids: selected });
                                }} className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm min-h-[90px]">
                                    {users.filter(u => u.id !== user?.id).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                                <p className="text-[10px] text-[#9CA3AF] mt-1">Tu ești adăugat automat.</p>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setShowNewChannel(false)} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button type="submit" disabled={saving}
                                    data-testid="create-channel-submit"
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se creează..." : "Creează canal"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

function ChannelItem({ channel, active, onClick }) {
    const unread = channel.unread_count || 0;
    return (
        <button onClick={onClick}
            data-testid={`channel-item-${channel.id}`}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors
                ${active ? "bg-[#002FA7]/10 text-[#002FA7] font-semibold" : "text-[#4B5563] hover:bg-[#F1F3F5] hover:text-[#111827]"}`}>
            <span className="flex-shrink-0">
                {channel.type === "team" && <Users size={14} />}
                {channel.type === "channel" && <Hash size={14} />}
                {channel.type === "dm" && <Avatar user={channel.display_user} size={20} />}
            </span>
            <span className="truncate flex-1 text-left">{channel.display_name}</span>
            {unread > 0 && (
                <span className="bg-[#002FA7] text-white text-[10px] rounded-full min-w-4 h-4 px-1.5 flex items-center justify-center">
                    {unread > 9 ? "9+" : unread}
                </span>
            )}
        </button>
    );
}
